import { useEffect, useState } from 'react';
import { showCustomModal } from '@atoms/ACustomModalInfo';
import { ArrowLeft } from '@carespace-icons/arrows/arrowLeft';
import { PlayCircle } from '@carespace-icons/media/playCircle';
import { Button, message, Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from '@carespace-icons/arrows/chevronDown';
import { Target04 } from '@carespace-icons/general/target04';
import { Calendar } from '@carespace-icons/time/calendar';
import { setActiveTab } from '@stores/aiAssistant/aiAssistant';
import { useTypedDispatch, useTypedSelector } from '@stores/index';
import { useLocation, useNavigate } from 'react-router-dom';
import { ACTIVETAB, INTAKE_STEPS } from '@stores/constants';
import { createProgram, selectExercise, selectProgram } from '@stores/aiAssistant/program';
import { IProgramExercise, ISelectedProgram, SelectedProgramProps } from '@stores/interfaces';
import { router } from '@routers/routers';
import { useBlockNavigation } from '@atoms/ABlockNavigation';
import './style.css'

const SelectedProgram = ({ state: customState }: SelectedProgramProps) => {
	const state = customState || useLocation();
	const { t } = useTranslation();
	const [showAllExercises, setShowAllExercises] = useState(false);
	const dispatch = useTypedDispatch();
	const navigate = useNavigate();
	const activeTab = useTypedSelector(
		state => state.aiAssistant.aiAssistant.activeTab,
	);
	const user = useTypedSelector(state => state.user);

	const handleEnroll = async (item: ISelectedProgram) => {
		const exercises: { strapiExerciseId: string; exerciseLibraryId: string; weeklyReps: number; dailyReps: number; sets: number; reps: number; order: number; }[] = [];
		item?.exercises?.forEach((exercise: IProgramExercise, index: number) => {
			const tempExercise = {
				strapiExerciseId:
					typeof exercise?.strapiExerciseId === 'object' && 'id' in exercise?.strapiExerciseId
						? String(exercise?.strapiExerciseId?.id)
						: String(exercise?.strapiExerciseId || ''),
				exerciseLibraryId: exercise.exerciseLibraryId || '',
				weeklyReps: exercise.weeklyReps,
				dailyReps: exercise.dailyReps,
				sets: exercise.sets,
				reps: exercise.reps,
				order: index + 1,
			};
			exercises.push(tempExercise);
		});
		const formData = new FormData();
		formData.append('name', item?.name);
		formData.append('duration', String(item?.duration));
		formData.append('durationType', item?.durationType);
		formData.append('description', item?.description);
		formData.append(
			'thumbnail',
			typeof item?.thumbnail === 'object' && 'url' in item?.thumbnail
				? item?.thumbnail.url
				: item?.thumbnail
		);
		if (user?.id) {
			formData.append('userId', String(user?.id));
		} else {
			console.error('userId is missing or invalid');
			return;
		}
		formData.append('strapiProgramTemplateId', item?.id);
		exercises?.forEach((exercise, index) => {
			Object.keys(exercise).forEach(key => {
				if (exercise[key as keyof typeof exercise]) {
					formData.append(`exercises[create][${index}][${key}]`, exercise[key as keyof typeof exercise] as string);
				}
			});
		});
		const data = await dispatch(createProgram(formData));
		data?.payload?.id &&
			message.success(t('Patient.data.newDashboard.enrolled'));
	};

	useEffect(() => {
		activeTab != ACTIVETAB.HOME && dispatch(setActiveTab(ACTIVETAB.HOME));
	}, [activeTab]);

	useEffect(() => {
		state?.state?.domain != INTAKE_STEPS.STEP_PROGRAM && window.scrollTo(0, 0);
	}, []);

	const buttonStyle = {
		color: '#533F85',
		border: 'inherit',
		width: '200px',
		height: '50px',
		marginTop: '6px',
		marginBottom: '6px',
	};

	const exercisesToShow = showAllExercises
		? state?.state?.selectedProgram?.exercises
		: state?.state?.selectedProgram?.exercises?.slice(0, 5);

	const popoverContent = (
		<div>
			<div className="text-left-align">
				<h3 style={{ fontSize: '18px' }}>
					<Target04 width={17} height={17} color="stroke-gray-700" />{' '}
					{t('Patient.data.newDashboard.functionalGoals')}
				</h3>
			</div>
			<hr style={{ margin: '8px 0' }} />
			{state?.state?.selectedProgram?.functional_goals?.map(
				(goal: { name: string }) => (
					<li
						key={goal.name}
						className="selected-program-goal-style">
						{goal?.name}
					</li>
				),
			)}
		</div>
	);
	const { clearNavigation } = useBlockNavigation();

	const startProgramSession = (item: {
		exercises: IProgramExercise[];
	}) => {
		dispatch(selectProgram(item));
		dispatch(selectExercise(item?.exercises));
		clearNavigation();
		navigate(`/${user.id}${router.AIASSISTANT_PROGRAM_START}`);
		dispatch(setActiveTab(''));
	};

	return (
		<div>
			<div className="selected-program-container">
				<div className="selected-program-inner-container zoom-animation">
					<div className="selected-program-div"></div>
					<img
						src={
							state?.state?.selectedProgram?.thumbnail?.url
								? state?.state?.selectedProgram?.thumbnail?.url
								: state?.state?.selectedProgram?.thumbnail ||
									(state?.state?.selectedProgram?.exercises[0] &&
										state?.state?.selectedProgram?.exercises[0]?.image)
						}
						alt={state?.state?.selectedProgram?.name}
						className={`selected-program-thumbnail ${
							state?.state?.selectedProgram?.thumbnail
								? 'object-cover'
								: 'object-contain'
						}`}
					/>
				</div>
				{state?.state?.domain != INTAKE_STEPS.STEP_PROGRAM && (
					<div
						className="selected-program-arrow-left"
						onClick={() => navigate(-1)}
						style={{ backgroundColor: 'var(--mainmenu-bg-color' }}>
						<ArrowLeft color="stroke-white" />
					</div>
				)}
				<div className="selected-program-content-container">
					<div className="selected-program-inner-content-container">
						<p className="calendar-container">
							<Calendar />
							{state?.state?.selectedProgram?.duration}{' '}
							{state?.state?.selectedProgram?.durationType
								? state?.state?.selectedProgram.duration > 1
									? state?.state?.selectedProgram.durationType
											.charAt(0)
											.toUpperCase() +
										state?.state?.selectedProgram.durationType.slice(1)
									: state?.state?.selectedProgram.durationType
											.charAt(0)
											.toUpperCase() +
										state?.state?.selectedProgram.durationType
											.slice(1)
											.replace(/s$/, '')
								: ''}
						</p>
					</div>
					<div style={{marginBottom: "1rem"}}>
						<p className="selected-program-name">
							{state?.state?.selectedProgram?.name}
						</p>
						<div className="selected-program-functional-goals-container">
							{state?.state?.selectedProgram?.functional_goals &&
								state?.state?.selectedProgram?.functional_goals[0] && (
									<p className="selected-program-goals-list">
										{state?.state?.selectedProgram?.functional_goals &&
											state?.state?.selectedProgram?.functional_goals[0]?.name}
									</p>
								)}
							{state?.state?.selectedProgram?.functional_goals &&
								state?.state?.selectedProgram?.functional_goals[1] && (
									<p className="selected-program-goals-list">
										{state?.state?.selectedProgram?.functional_goals &&
											state?.state?.selectedProgram?.functional_goals[1]?.name}
									</p>
								)}
							{state?.state?.selectedProgram?.functional_goals?.length! > 2 && (
								<Popover
									content={popoverContent}
									trigger="hover"
									placement="topLeft">
									<p className="selected-program-goals-list-show-all-button">
										{t('Patient.data.newDashboard.showAll')}
									</p>
								</Popover>
							)}
						</div>
					</div>
					<Popover
						content={state?.state?.selectedProgram?.description}
						trigger="hover"
						placement="topLeft"
						overlayInnerStyle={{
							maxWidth: '50vw',
							whiteSpace: 'normal',
						}}
						overlayStyle={{
							overflowX: 'hidden',
						}}
						autoAdjustOverflow={true}>
						<p className="selected-program-patient-description">
							{state?.state?.selectedProgram?.description}
						</p>
					</Popover>
					{state?.state?.domain != INTAKE_STEPS.STEP_PROGRAM && (
						<Button
							className="start-session-css"
							style={buttonStyle}
							onClick={e => {
								e.stopPropagation();
								{
									state?.state?.domain === INTAKE_STEPS.SUGGESTED
										? handleEnroll(state?.state?.selectedProgram)
										: startProgramSession(state?.state?.selectedProgram);
								}
							}}>
							<span
								className="selected-program-patient-heading"
								style={{ color: 'var(--button-text-color)' }}>
								{state?.state?.domain === INTAKE_STEPS.SUGGESTED
									? t('Patient.data.newDashboard.enroll')
									: t('Patient.data.myProgress.rehab.startSession')}
							</span>
						</Button>
					)}
				</div>
			</div>
			<div style={{padding: '1.25rem'}}>
				<p className="font-inter text-xl font-bold leading-[30px] text-left text-gray-700">
					{state?.state?.selectedProgram?.exercises?.length > 1
						? t('Patient.data.newDashboard.exercises')
						: t('Patient.data.newDashboard.exercise')}
				</p>
			</div>
			<div style={{padding: '1.25rem'}}>
				{exercisesToShow?.map(
					(
						item: {
							assets?: {
								video?: {
									url: string;
								},
								image?: {
									url: string;
								};
							}[];
							name: string;
							description: string;
							strapiExerciseId: {
								exercise_image: { url: string }[];
								exercise_video: { url: string }[];
								name: string;
								description: string;
							};
							weeklyReps: string;
							dailyReps: string;
							sets: string;
							reps: string;
						},
						index: number,
					) => (
						<div key={index}>
	<div className="selected-program-list-wrapper">
		<div className="selected-program-list-index-container">
			<p className="selected-program-list-index">{index + 1}</p>
		</div>
		<div className="selected-program-list-image-section">
			<div className="selected-program-list-image-container">
				<div
					onClick={() =>
						showCustomModal({
							video:
								(item?.assets &&
									item?.assets[0]?.video?.url) ||
								item?.strapiExerciseId?.exercise_video[0]?.url,
							name: item?.strapiExerciseId?.name || item?.name,
							description:
								item?.strapiExerciseId?.description ||
								item?.description,
						})
					}
					className="selected-program-list-image-wrapper">
					<img
						src={
							item?.assets
								? item?.assets[0]?.image?.url
								: item?.strapiExerciseId?.exercise_image?.[0]?.url ||
									state?.state?.selectedProgram?.thumbnail?.url ||
									state?.state?.selectedProgram?.thumbnail
						}
					/>
					<div className="selected-program-list-play-button">
						<PlayCircle width={50} height={50} />
					</div>
				</div>
			</div>
		</div>
		<div className="selected-program-list-text-section">
			<p className="selected-program-list-title">
				{item?.strapiExerciseId?.name || item?.name}
			</p>
			<p className="selected-program-list-stats">
				{t('Patient.data.rehab.weekly').charAt(0).toUpperCase()}
				{t('Patient.data.rehab.weekly').slice(1).toLowerCase()} {item?.weeklyReps}X{' '}
				{t('Patient.data.rehab.daily').charAt(0).toUpperCase()}
				{t('Patient.data.rehab.daily').slice(1).toLowerCase()} {item?.dailyReps}X{' '}
				{t('Patient.data.rehab.sets').charAt(0).toUpperCase()}
				{t('Patient.data.rehab.sets').slice(1).toLowerCase()} {item?.sets}X{' '}
				{t('Patient.data.rehab.reps').charAt(0).toUpperCase()}
				{t('Patient.data.rehab.reps').slice(1).toLowerCase()} {item?.reps}X
			</p>
			<p className="selected-program-list-description">
				{item?.strapiExerciseId?.description || item?.description}
			</p>
		</div>
	</div>
	<hr />
</div>

					),
				)}
				{!showAllExercises &&
					state?.state?.selectedProgram?.exercises?.length! > 5 && (
						<div className="selected-program-down-arrow">
							<button
								className="selected-program-down-arrow-button"
								onClick={() => setShowAllExercises(true)}>
								<ChevronDown />
							</button>
						</div>
					)}
			</div>
		</div>
	);
};

export default SelectedProgram;
